import React, { useEffect, useMemo, useState } from 'react';
import api from '../../helpers/api_helper';
import { useAppDispatch, useAppSelector } from '../../slices/types';
import { sendNotification, clearNotificationResult, type SendNotificationPayload } from '../../slices/notifications/notificationsSlice';
import type { User } from '../../slices/users/adminUsersSlice';

type Scope = 'broadcast' | 'users' | 'user';
type NotificationType = 'system' | 'offer';

interface SendNotificationProps {
  users: User[];
  defaultUser?: User | null;
  onClose: () => void;
}

const SendNotification: React.FC<SendNotificationProps> = ({ users, defaultUser, onClose }) => {
  const dispatch = useAppDispatch();
  const { sending, error, lastResult } = useAppSelector(s => s.Notifications);
  const [notifyScope, setNotifyScope] = useState<Scope>(defaultUser ? 'user' : 'users');
  const [type, setType] = useState<NotificationType>('system');
  const [title, setTitle] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [singleUserId, setSingleUserId] = useState<number | null>(defaultUser ? defaultUser.id : null);
  const [dataJson, setDataJson] = useState<string>('');
  const [apps, setApps] = useState<{ id: string; app_name: string; version: string }[]>([]);
  const [appsLoading, setAppsLoading] = useState<boolean>(false);
  const [appsError, setAppsError] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  // Fetch app list for Broadcast scope (COMMENTED OUT)
  // useEffect(() => {
  //   if (notifyScope !== 'broadcast') return;
  //   let isMounted = true;
  //   const fetchApps = async () => {
  //     try {
  //       setAppsLoading(true);
  //       setAppsError('');
  //       const res = await api.get('/apps/');
  //       if (!isMounted) return;
  //       const list = Array.isArray(res.data) ? res.data : [];
  //       setApps(list);
  //       // Preserve previously selected app if still present, else reset
  //       if (!list.find((a: any) => a.id === selectedAppId)) {
  //         setSelectedAppId('');
  //       }
  //     } catch (err: any) {
  //       if (!isMounted) return;
  //       setAppsError(err?.message || 'Failed to load apps');
  //     } finally {
  //       if (isMounted) setAppsLoading(false);
  //     }
  //   };
  //   fetchApps();
  //   return () => { isMounted = false; };
  // }, [notifyScope]);

  useEffect(() => {
    if (defaultUser) {
      setNotifyScope('user');
      setSingleUserId(defaultUser.id);
    }
  }, [defaultUser]);

  // Show success then navigate back automatically on successful send
  useEffect(() => {
    if (lastResult && !sending) {
      setShowSuccess(true);
      const t = setTimeout(() => {
        onClose();
        setShowSuccess(false);
        // clear stored result so next open is clean
        dispatch(clearNotificationResult());
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [lastResult, sending, dispatch, onClose]);

  const isValid = () => {
    if (!message.trim()) return false;
    // if (notifyScope === 'broadcast' && !selectedAppId) return false; // COMMENTED OUT
    if (notifyScope === 'users' && selectedUserIds.length === 0) return false;
    if (notifyScope === 'user' && !singleUserId) return false;
    return true;
  };

  const handleSend = async () => {
    const payload: SendNotificationPayload = (() => {
      // if (notifyScope === 'broadcast') { // COMMENTED OUT
      //   return { scope: 'broadcast', title, message, type, app_id: selectedAppId };
      // }
      if (notifyScope === 'users') {
        let parsed: any | undefined;
        try { parsed = dataJson ? JSON.parse(dataJson) : undefined; } catch { parsed = undefined; }
        return { scope: 'users', user_ids: selectedUserIds, title, message, type, data: parsed };
      }
      return { scope: 'user', user_id: singleUserId as number, title, message, type };
    })();

    try {
      await dispatch(sendNotification(payload)).unwrap();
    } catch (e) {
      // handled by slice state
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border mb-6">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-semibold">Send Notification</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
      </div>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Scope</label>
            <select
              value={notifyScope}
              onChange={(e)=>{
                const scope = e.target.value as Scope;
                setNotifyScope(scope);
                // if(scope==='broadcast'){ setSelectedUserIds([]); setSingleUserId(null); } // COMMENTED OUT
                if(scope==='users'){ setSelectedAppId(''); setSingleUserId(null); }
                if(scope==='user'){ setSelectedAppId(''); setSelectedUserIds([]); }
              }}
              className="w-full px-3 py-2 border rounded-md"
            >
              {/* <option value="broadcast">Broadcast</option> */} {/* COMMENTED OUT */}
              <option value="users">Users</option>
              <option value="user">User</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select value={type} onChange={(e)=> setType(e.target.value as NotificationType)} className="w-full px-3 py-2 border rounded-md">
              <option value="system">System</option>
              <option value="offer">Offer</option>
            </select>
          </div>
        </div>

        {/* Broadcast scope - Select App section (COMMENTED OUT) */}
        {/* {notifyScope === 'broadcast' && (
          <div>
            <label className="block text-sm font-medium mb-2">Select App</label>
            <div className="space-y-2 max-h-48 overflow-auto border rounded-md p-3">
              {appsLoading && <div className="text-sm text-gray-500">Loading apps…</div>}
              {appsError && <div className="text-sm text-red-600">{appsError}</div>}
              {!appsLoading && !appsError && apps.length === 0 && (
                <div className="text-sm text-gray-500">No apps found.</div>
              )}
              {!appsLoading && !appsError && apps.map(app => (
                <label key={app.id} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="app" checked={selectedAppId===app.id} onChange={()=>setSelectedAppId(app.id)} />
                  <span className="font-medium">{app.app_name}</span>
                  <span className="ml-auto text-xs text-gray-500">{app.id}</span>
                </label>
              ))}
            </div>
          </div>
        )} */}

        {notifyScope === 'users' && (
          <div>
            <label className="block text-sm font-medium mb-2">Select Users</label>
            <div className="border rounded-md max-h-64 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 w-10"><input type="checkbox" onChange={(e)=>{
                      if(e.target.checked){ setSelectedUserIds(users.map(u=>u.id)); }
                      else { setSelectedUserIds([]); }
                    }} checked={selectedUserIds.length>0 && selectedUserIds.length===users.length} /></th>
                    <th className="px-3 py-2 text-left">User</th>
                    <th className="px-3 py-2 text-left">Mobile</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="odd:bg-white even:bg-gray-50">
                      <td className="px-3 py-2"><input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={(e)=>{
                        setSelectedUserIds(prev=> e.target.checked ? [...prev, u.id] : prev.filter(id=>id!==u.id));
                      }} /></td>
                      <td className="px-3 py-2">{u.full_name}</td>
                      <td className="px-3 py-2">{u.mobile_number}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-xs text-gray-600">{selectedUserIds.length} users selected</div>
          </div>
        )}

        {notifyScope === 'user' && (
          <div>
            <label className="block text-sm font-medium mb-1">User</label>
            <select value={singleUserId ?? ''} onChange={(e)=> setSingleUserId(Number(e.target.value)||null)} className="w-full px-3 py-2 border rounded-md">
              <option value="">Select user</option>
              {users.map(u=> (
                <option key={u.id} value={u.id}>{u.full_name} ({u.mobile_number})</option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e)=> setTitle(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="Enter title"
            />
          </div>
          {notifyScope === 'users' && (
            <div>
              <label className="block text-sm font-medium mb-1">Data (optional JSON)</label>
              <input
                type="text"
                value={dataJson}
                onChange={(e)=> setDataJson(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
                placeholder='e.g. {"promo_code":"EXTRA2"}'
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Message</label>
          <textarea
            value={message}
            onChange={(e)=> setMessage(e.target.value)}
            className="w-full px-3 py-2 border rounded-md h-32 resize-y"
            placeholder="Write your message..."
          />
        </div>

        {showSuccess && lastResult && (
          <div className="p-3 border rounded-md bg-green-50 text-green-800 text-sm">
            Sent notifications successfully. IDs: {lastResult.created_ids.join(', ')}
          </div>
        )}
        {error && (
          <div className="p-3 border rounded-md bg-red-50 text-red-800 text-sm">
            {error}
            <button className="ml-2 underline" onClick={()=>dispatch(clearNotificationResult())}>Dismiss</button>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            onClick={handleSend}
            disabled={!isValid() || sending}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendNotification;


